import React, { useState } from 'react';
import { Handle, Position } from 'reactflow';
import { usePipeline } from '../PipelineContext';

const ACTION_LABELS = {
  update_field:          { icon: '✏️', text: 'Update field' },
  send_notification:     { icon: '📥', text: 'Send to inbox' },
  create_record:         { icon: '➕', text: 'Create record' },
  create_function_task:  { icon: '📋', text: 'Create task' },
  update_function_task:  { icon: '📝', text: 'Update task' },
  http_request:          { icon: '🌐', text: 'HTTP request' },
  github_create_issue:   { icon: '🐙', text: 'Create issue' },
  github_create_pr:      { icon: '🔀', text: 'Create PR' },
  github_push_file:      { icon: '📄', text: 'Push file' },
};

export default function ActionNode({ id, data, selected }) {
  const [showInfo, setShowInfo] = useState(false);
  const pipeline = usePipeline();
  const { opsMode } = pipeline || {};

  const act = ACTION_LABELS[data.actionType || 'update_field'] || ACTION_LABELS.update_field;
  const headerStyle = data.color ? { background: data.color + '22', color: data.color } : {};
  const nodeStyle   = data.color ? { borderColor: data.color } : {};

  // Warn on the canvas when an action that writes to a collection (update_field /
  // create_record) has no target — neither an edge to a Collection nor one
  // chosen in its panel. This is the silent gotcha that used to just fail in Logs.
  const { nodes, edges } = pipeline || {};
  const needsCollection = data.actionType === 'update_field' || data.actionType === 'create_record';
  const hasCollectionTarget = !!data.collectionId || (edges || []).some(e =>
    e.source === id && (nodes || []).some(n => n.id === e.target && n.type === 'collectionNode'));
  const showTargetWarning = needsCollection && !hasCollectionTarget;

  const summary = data.actionType === 'update_field' && data.fieldName && data.fieldValue
    ? `${data.filterFieldName && data.filterValue ? `Where ${data.filterFieldName}="${data.filterValue}" → ` : ''}Set ${data.fieldName} → "${data.fieldValue}"`
    : data.actionType === 'send_notification' && data.notificationText
    ? `"${data.notificationText.slice(0, 40)}${data.notificationText.length > 40 ? '...' : ''}"`
    : data.actionType === 'create_function_task' && data.functionNodeId
    ? 'Creates task in function queue'
    : data.actionType === 'update_function_task' && data.functionNodeId
    ? 'Updates task in function queue'
    : data.actionType === 'http_request' && (data.httpEndpoint || data.connectionNodeId)
    ? `${data.httpMethod || 'GET'} ${data.httpEndpoint || 'via connection'}`
    : data.actionType === 'github_create_issue' && data.issueTitle
    ? `"${data.issueTitle.slice(0, 40)}"`
    : data.actionType === 'github_create_pr' && data.prHead
    ? `${data.prHead} → ${data.prBase || 'main'}`
    : data.actionType === 'github_push_file' && data.filePath
    ? data.filePath
    : null;

  return (
    <div className={`pipeline-node action-node ${selected ? 'selected' : ''}`} style={nodeStyle}>
      <Handle type="target" position={Position.Left} className="node-handle" />

      <div className="node-header action-header" style={headerStyle}>
        <span className="node-type-icon">⚙</span>
        <span className="node-type-label">{opsMode ? 'Auto Step' : 'Action'}</span>
        <button
          className="node-info-btn"
          onMouseDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); setShowInfo(s => !s); }}
        >ⓘ</button>
      </div>

      <div className="node-body">
        <div className="node-title">{data.label || 'Action'}</div>
        {data.description && <div className="node-description">{data.description}</div>}

        {showInfo && (
          <div className="node-info-card">
            <div className="node-info-what">Performs an automated step — no human or AI needed. Use for data updates, API calls, task creation, and external integrations.</div>
            <div className="node-info-eg-label">Action types:</div>
            <div className="node-info-eg">→ Update field / Create record — collection data</div>
            <div className="node-info-eg">→ Create/update task — function task queues</div>
            <div className="node-info-eg">→ HTTP request — call external APIs</div>
            <div className="node-info-eg">→ GitHub — issues, PRs, file pushes</div>
            <div className="node-info-eg">→ Send to inbox — user notifications</div>
          </div>
        )}

        <div className="action-type-chip">
          <span className="action-chip-icon">{act.icon}</span>
          <span>{act.text}</span>
        </div>
        {summary && <div className="action-summary">{summary}</div>}
        {showTargetWarning && (
          <div className="action-warning" title="Connect this action to a Collection, or pick one in its panel, so it knows where to write.">
            ⚠ Not connected to a collection
          </div>
        )}
      </div>

      <Handle type="source" position={Position.Right} className="node-handle" />
    </div>
  );
}
